import { useState, useEffect, useCallback, useRef } from "react";
import { useCustom, useTranslate, useNotification } from "@refinedev/core";
import type {
  ItemTypeMetadata,
  RelationshipMetadata,
  RelationshipItemData,
} from "@/types";
import { useRelationshipMutations } from "./useRelationshipMutations";

/** Describes the discovered source from which to populate relationship items */
export interface PopulateSource {
  /** The reference field name on the source item (camelCase, e.g., "classNumber") — only for reference-field sources */
  referenceField?: string;
  /** The relationship name on the source item (e.g., "Rel_AttendanceClass") — only for relationship sources */
  sourceRelationshipName?: string;
  /** The referenced/related ItemType name (e.g., "GoKids Class") */
  referencedItemType: string;
  /** The relationship on the referenced item that has matching related type */
  referencedRelationshipName: string;
  /** Human-readable label for the button */
  label: string;
}

interface UsePopulateRelationshipOptions {
  /** The source ItemType name (e.g., "Attendance") */
  itemType: string;
  /** The source item ID */
  itemId: string;
  /** The current relationship metadata */
  relationship: RelationshipMetadata;
  /** Already loaded relationship items (to filter out duplicates) */
  existingItems: RelationshipItemData[];
  /** Whether to enable discovery */
  enabled?: boolean;
}

/**
 * Hook that discovers and executes "Populate from Reference" functionality.
 *
 * Discovery algorithm (fully dynamic, no hardcoded values):
 * 1. Fetch source item metadata → find `item`-type properties (reference fields) AND relationships
 * 2. For each reference field / relationship → fetch referenced ItemType metadata
 * 3. Check if referenced type has a relationship matching current tab's relatedItemTypeName
 * 4. If match found → expose populate action
 *
 * Populate flow (multi-source):
 * 1. User selects one or more source items via ItemPickerModal
 * 2. Fetch related items from all selected sources in parallel
 * 3. Deduplicate across sources and filter out existing items
 * 4. Bulk-add remaining items
 */
export const usePopulateRelationship = ({
  itemType,
  itemId,
  relationship,
  existingItems,
  enabled = true,
}: UsePopulateRelationshipOptions) => {
  const translate = useTranslate();
  const { open: openNotification } = useNotification();
  const { bulkAddRelationshipItems } = useRelationshipMutations();

  const [populateSource, setPopulateSource] = useState<PopulateSource | null>(
    null,
  );
  const [isPopulating, setIsPopulating] = useState(false);
  const discoveryDone = useRef(false);

  // Step 1: Fetch source item metadata to find reference fields and relationships
  const { data: sourceMetadata } = useCustom<ItemTypeMetadata>({
    url: `/meta/itemtypes/${itemType}`,
    method: "get",
    queryOptions: {
      enabled: enabled && !!relationship.relatedItemTypeName,
      staleTime: 30 * 60 * 1000, // 30 minutes cache
    },
  });

  // Step 2: Discover populate source from metadata
  useEffect(() => {
    if (discoveryDone.current) return;
    if (!sourceMetadata?.data) return;
    if (!relationship.relatedItemTypeName) return;

    const metadata = sourceMetadata.data as ItemTypeMetadata;

    // Find all "item" type properties (reference fields)
    const referenceFields = (metadata.properties || []).filter(
      (p) => p.dataType === "item" && p.dataSource,
    );

    // Also consider relationships on the source ItemType as populate sources
    // e.g., Rel_AttendanceClass → Class → Rel_ClassStudent → Student
    const sourceRelationships = (metadata.relationships || []).filter(
      (r) =>
        r.relatedItemTypeName &&
        r.relatedItemTypeName !== relationship.relatedItemTypeName &&
        r.name !== relationship.name,
    );

    if (referenceFields.length === 0 && sourceRelationships.length === 0)
      return;

    discoverPopulateSource(
      referenceFields,
      sourceRelationships,
      relationship.relatedItemTypeName,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMetadata?.data, relationship.relatedItemTypeName]);

  // Discover the populate source by checking reference fields AND relationships
  const discoverPopulateSource = useCallback(
    async (
      referenceFields: Array<{
        name: string;
        label?: string;
        dataSource?: string;
      }>,
      sourceRelationships: RelationshipMetadata[],
      targetRelatedType: string,
    ) => {
      if (discoveryDone.current) return;
      discoveryDone.current = true;

      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) return;

      // Priority 1: Check relationships on source ItemType (e.g., Rel_AttendanceClass)
      // This is preferred because it supports multiple linked items natively
      for (const rel of sourceRelationships) {
        if (!rel.relatedItemTypeName) continue;

        try {
          const response = await fetch(
            `/api/meta/itemtypes/${encodeURIComponent(rel.relatedItemTypeName)}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            },
          );

          if (!response.ok) continue;

          const refMetadata: ItemTypeMetadata = await response.json();
          if (!refMetadata.relationships) continue;

          const matchingRel = refMetadata.relationships.find(
            (r) => r.relatedItemTypeName === targetRelatedType,
          );

          if (matchingRel) {
            setPopulateSource({
              sourceRelationshipName: rel.name,
              referencedItemType: rel.relatedItemTypeName,
              referencedRelationshipName: matchingRel.name,
              label: rel.tabLabel || rel.relatedItemTypeName,
            });
            return;
          }
        } catch {
          continue;
        }
      }

      // Priority 2: Check reference fields (fallback for single-reference pattern)
      for (const field of referenceFields) {
        if (!field.dataSource) continue;

        try {
          const response = await fetch(
            `/api/meta/itemtypes/${encodeURIComponent(field.dataSource)}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            },
          );

          if (!response.ok) continue;

          const refMetadata: ItemTypeMetadata = await response.json();
          if (!refMetadata.relationships) continue;

          const matchingRel = refMetadata.relationships.find(
            (r) => r.relatedItemTypeName === targetRelatedType,
          );

          if (matchingRel) {
            setPopulateSource({
              referenceField: snakeToCamel(field.name),
              referencedItemType: field.dataSource,
              referencedRelationshipName: matchingRel.name,
              label: field.label || field.dataSource,
            });
            return;
          }
        } catch {
          continue;
        }
      }
    },
    [],
  );

  /**
   * Get the currently linked source item IDs for pre-selection in the picker.
   * - If source is a relationship (Rel_AttendanceClass): fetches all related IDs
   * - If source is a reference field (class_number): returns single ID
   */
  const getPreSelectedIds = useCallback(async (): Promise<string[]> => {
    if (!populateSource) return [];

    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return [];

    try {
      // Relationship source: fetch all related IDs from the relationship
      if (populateSource.sourceRelationshipName) {
        const relResponse = await fetch(
          `/api/items/${encodeURIComponent(itemType)}/${itemId}/relationships/${encodeURIComponent(populateSource.sourceRelationshipName)}?pageSize=200`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        );

        if (!relResponse.ok) return [];

        const relData = await relResponse.json();
        const items: Array<{ relatedId?: string }> = relData.items || [];
        return items
          .map((item) => item.relatedId)
          .filter(Boolean) as string[];
      }

      // Reference field source: fetch parent item and read field value
      if (populateSource.referenceField) {
        const parentResponse = await fetch(
          `/api/items/${encodeURIComponent(itemType)}/${itemId}`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        );

        if (!parentResponse.ok) return [];

        const parentData = await parentResponse.json();
        const referenceId = parentData[populateSource.referenceField];
        return referenceId ? [referenceId] : [];
      }

      return [];
    } catch {
      return [];
    }
  }, [populateSource, itemType, itemId]);

  /**
   * Populate relationship items from multiple source items.
   * Fetches related items from each source in parallel, deduplicates, and bulk-adds.
   */
  const populateFromItems = useCallback(
    async (sourceItemIds: string[]): Promise<boolean> => {
      if (!populateSource || sourceItemIds.length === 0) return false;

      setIsPopulating(true);
      try {
        const accessToken = localStorage.getItem("access_token");
        if (!accessToken) {
          openNotification?.({
            type: "error",
            message: translate("notifications.error", "Error"),
            description: translate(
              "notifications.unauthorized",
              "Not authenticated",
            ),
          });
          return false;
        }

        // Fetch related items from all sources in parallel
        const fetchPromises = sourceItemIds.map((sourceId) =>
          fetch(
            `/api/items/${encodeURIComponent(
              populateSource.referencedItemType,
            )}/${sourceId}/relationships/${encodeURIComponent(
              populateSource.referencedRelationshipName,
            )}?pageSize=200`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            },
          ).then(async (res) => {
            if (!res.ok) throw new Error(`Failed to fetch from ${sourceId}`);
            const data = await res.json();
            return (data.items || []) as Array<{ relatedId?: string }>;
          }),
        );

        const results = await Promise.allSettled(fetchPromises);

        // Collect all related IDs, deduplicating across sources
        const allRelatedIds = new Set<string>();
        let successCount = 0;
        let failCount = 0;

        for (const result of results) {
          if (result.status === "fulfilled") {
            successCount++;
            for (const item of result.value) {
              if (item.relatedId) {
                allRelatedIds.add(item.relatedId);
              }
            }
          } else {
            failCount++;
          }
        }

        if (successCount === 0) {
          openNotification?.({
            type: "error",
            message: translate("notifications.error", "Error"),
            description: translate(
              "notifications.populateNoItems",
              { source: populateSource.label },
              `No items found in ${populateSource.label}`,
            ),
          });
          return false;
        }

        // Filter out items already in the current relationship
        const existingRelatedIds = new Set(
          existingItems
            .map((item) => item.relatedId)
            .filter(Boolean) as string[],
        );

        const newRelatedIds = Array.from(allRelatedIds).filter(
          (id) => !existingRelatedIds.has(id),
        );

        if (newRelatedIds.length === 0) {
          openNotification?.({
            type: "success",
            message: translate(
              "notifications.populateAllExist",
              "All items already exist in the list",
            ),
          });
          return true;
        }

        // Bulk add all unique items in one call
        await bulkAddRelationshipItems({
          itemType,
          itemId,
          relationshipName: relationship.name,
          items: newRelatedIds.map((relatedId) => ({ relatedId })),
        });

        if (failCount > 0) {
          openNotification?.({
            type: "success",
            message: translate(
              "notifications.populatePartialSuccess",
              { count: newRelatedIds.length },
              `Added ${newRelatedIds.length} items (some sources failed to load)`,
            ),
          });
        } else {
          openNotification?.({
            type: "success",
            message: translate(
              "notifications.populateSuccess",
              { count: newRelatedIds.length },
              `Successfully added ${newRelatedIds.length} items`,
            ),
          });
        }

        return true;
      } catch (error) {
        openNotification?.({
          type: "error",
          message: translate(
            "notifications.populateError",
            "Failed to populate items",
          ),
          description: error instanceof Error ? error.message : undefined,
        });
        return false;
      } finally {
        setIsPopulating(false);
      }
    },
    [
      populateSource,
      itemType,
      itemId,
      relationship.name,
      existingItems,
      bulkAddRelationshipItems,
      openNotification,
      translate,
    ],
  );

  return {
    /** The discovered populate source, or null if not applicable */
    populateSource,
    /** Whether a populate operation is in progress */
    isPopulating,
    /** Get pre-selected source item IDs (from relationship or reference field) */
    getPreSelectedIds,
    /** Populate from multiple source items. Returns true on success. */
    populateFromItems,
  };
};

/** Convert snake_case to camelCase */
const snakeToCamel = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
};
